import { useState } from 'react';
import { PageHero } from '@/components/PageHero';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/components/Seo';
import { CtaBanner } from '@/components/CtaBanner';
import { GUIDE_IMAGES, GUIDE_HIGHLIGHTS } from '@/data/site';
import { STATIC_IT } from '@/data/staticIt';
import { STATIC_EN } from '@/data/staticEn';
import { useLang, LangLink } from '@/i18n/LangContext';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { SALUTE_PAGES } from '@/data/salute';
import { PRIVATI_PAGES } from '@/data/privati';
import { AZIENDE_PAGES, PREVIDENZA_PAGE } from '@/data/aziende';
import { SALUTE_PAGES_EN } from '@/data/en/salute';
import { PRIVATI_PAGES_EN } from '@/data/en/privati';
import { AZIENDE_PAGES_EN, PREVIDENZA_PAGE_EN } from '@/data/en/aziende';

const IT_REGISTRY = [...SALUTE_PAGES, ...PRIVATI_PAGES, ...AZIENDE_PAGES, PREVIDENZA_PAGE];
const EN_REGISTRY = [...SALUTE_PAGES_EN, ...PRIVATI_PAGES_EN, ...AZIENDE_PAGES_EN, PREVIDENZA_PAGE_EN];

export default function GuideIndex() {
  const { lang, t } = useLang();
  const c = lang === 'en' ? STATIC_EN.guideIndex : STATIC_IT.guideIndex;
  const registry = lang === 'en' ? EN_REGISTRY : IT_REGISTRY;
  const [filter, setFilter] = useState('all');
  useSeo(c.seo);

  const guides = registry.filter((p) => p.kind === 'guide' || p.kind === 'service');
  const highlights = GUIDE_HIGHLIGHTS.map((p) => registry.find((page) => page.path === p)).filter(Boolean);
  const areas = [...new Set(guides.map((g) => g.area))];
  const visible = filter === 'all' ? guides : guides.filter((g) => g.area === filter);

  return (
    <div data-testid="guide-index-page">
      <PageHero
        eyebrow={c.eyebrow}
        title={c.title}
        summary={c.summary}
        image="/assets/area-privati.webp"
        imageAlt={c.title}
        breadcrumbs={[{ label: t('breadcrumbHome'), href: '/' }, { label: c.eyebrow }]}
      />

      <section className="mx-auto max-w-site px-5 md:px-8 py-14 md:py-20">
        <Reveal className="mb-12">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-6">{t('highlights')}</p>
          <div className="grid md:grid-cols-2 gap-px bg-border border border-border rounded-lg overflow-hidden">
            {highlights.map((g) => (
              <LangLink
                key={g.path}
                to={g.path}
                data-testid={`highlight-${g.path.replace(/\//g, '-')}`}
                className="group bg-white block"
              >
                <div className="overflow-hidden aspect-[16/8]">
                  <img
                    src={GUIDE_IMAGES[g.path]}
                    alt={g.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-light mb-3">{g.eyebrow}</p>
                  <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight mb-3 text-balance group-hover:text-primary transition-colors duration-200">{g.title}</h2>
                  <p className="text-sm md:text-base leading-relaxed text-foreground/65 mb-5">{g.summary}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                    {t('readGuide')}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </LangLink>
            ))}
          </div>
        </Reveal>

        <Reveal className="flex flex-wrap gap-2 mb-10" data-testid="guide-filters">
          {[c.filterAll, ...areas].map((label) => {
            const value = label === c.filterAll ? 'all' : label;
            return (
              <button
                key={value}
                type="button"
                data-testid={`filter-${value.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setFilter(value)}
                aria-pressed={filter === value}
                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors duration-200 ${
                  filter === value ? 'bg-brand-deep text-white border-brand-deep' : 'bg-white border-border hover:border-primary hover:text-primary'
                }`}
              >
                {label}
              </button>
            );
          })}
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="guide-grid">
          {visible.map((g, i) => (
            <Reveal key={g.path} delay={i * 0.04}>
              <LangLink
                to={g.path}
                data-testid={`guide-card-${g.path.replace(/\//g, '-')}`}
                className="group block bg-white border border-border rounded-lg overflow-hidden h-full hover:border-primary transition-colors duration-300"
              >
                <div className="overflow-hidden aspect-[16/9]">
                  <img
                    src={GUIDE_IMAGES[g.path]}
                    alt={g.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-light mb-3">{g.area}</p>
                  <h3 className="font-heading text-lg font-bold tracking-tight mb-3 text-balance group-hover:text-primary transition-colors duration-200">{g.title}</h3>
                  <p className="text-sm leading-relaxed text-foreground/65 mb-5">{g.summary}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                    {t('readGuide')}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </div>
              </LangLink>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="mx-auto max-w-site px-5 md:px-8">
        <CtaBanner
          title={lang === 'en' ? 'Can\u2019t find the answer you need?' : 'Non trovi la risposta che cerchi?'}
          text={lang === 'en' ? 'Tell us your situation in two minutes: we will point you in the right direction, with no obligation.' : 'Raccontaci la tua situazione in due minuti: ti indichiamo la strada giusta, senza impegno.'}
          label={t('ctaHeader')}
        />
      </div>
    </div>
  );
}
